import { useLazyQuery, useMutation } from '@apollo/client';
import React, {
  ComponentProps,
  FunctionComponent,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import * as entities from '../../graphql/entities';
import {
  BinLocation,
  IssueReceipt as IssueReceiptEntity,
  Product,
  SalesCredit,
  StockAdjustment,
} from '../../graphql/entities';
import createForm from '../../components/connectedForm/ConnectedForm';
import DropdownEditor from '../../components/DropdownEditor';
import { default as Form } from '../../components/connectedForm';
import * as input from '../../components/connectedForm/inputComponents';
import Formatters from '../../components/grid-addons/formatters';
import StockLotsByBin from '../../graphql/queries/product/StockLotsByBin.graphql';
import ConnectedFormContext, { ConnectedFormContextType } from '../../components/Context/ConnectedFormContext';
import getLotAttributeColumns, { getAttrIdArray, getKey } from '../../utils/getLotAttributeColumns';
import getIn from '../../components/connectedForm/utils/getIn';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import { PrimaryButton } from '../../components/connectedForm/Button';
import BlurTextField from '../../components/connectedForm/BlurTextField';
import ProductComponentAllComponents from '../../graphql/queries/product/ProductComponentAllComponents.graphql';
import BinLocationForProductAndWarehouse from '../../graphql/queries/product/BinLocationForProductAndWarehouse.graphql';
import ProductWarehouseDetailsQuery from '../../graphql/queries/product/ProductWarehouseDetailsWorkspaceQuery.graphql';
import GoodsReceiptAttributesQuery from '../../graphql/queries/product/GoodsReceiptAttributesQuery.graphql';
import ContextMenuItem from '../../components/ContextMenuItem';
import GridContainer from '../../components/connectedForm/GridContainer';
import { useDebouncedTemporarySave } from '../../components/connectedForm/utils/handleTemporarySave';
import getProductDP from '../../utils/getProductDP';
import { useFormQuery } from '../../graphql/containers/useFormQuery';
import {
  ActionType,
  AlertType,
  BooleanOperator,
  FilterOperation,
  IFilterInput,
  IGoodsReceiptAttributesQueryResult,
  IGoodsReceiptAttributesQueryVariables,
  ILotAttributeConfig,
  ILotAttributeValue,
  InputMaybe,
  IProduct,
  IProductComponentAllComponentsQueryResult,
  IProductComponentAllComponentsQueryVariables,
  IProductInput,
  IProductWarehouseDetail,
  IProductWarehouseDetailsWorkspaceQueryResult,
  IProductWarehouseDetailsWorkspaceQueryVariables,
  IssueReceipt,
  IStockAdjustment,
  IStockAdjustmentLine,
  IStockAdjustmentLineInput,
  IStockLot,
  IStockLotsByBinQueryResult,
  IStockLotsByBinQueryVariables,
  IWarehouse,
  KitOpt,
  Maybe,
  ProductClass,
} from '../../graphql/schema/p4types';
import { uuIdGenerator } from '../../utils/UuIdGenerator';
import { BlurTextFieldType, IBlurTextFieldProps } from '../../components/connectedForm/BlurTextField/BlurTextField';
import { useCompany } from '../../utils/FetchConfig';
import CheckboxInput from '../../components/connectedForm/CheckboxInput';
import StockAdjustmentLabel from '../../graphql/entities/product/StockAdjustmentLabel';
import { IExtendedMetaAction, useProductsStockAdjustmentPostSave } from './useProductsStockAdjustmentPostSave';
import { usePerformSave } from '../../components/connectedForm/Hooks/usePerformSave';
import { InfoTheme } from '../../styles/themes/default/defaultFluentTheme';
import { serviceActions } from '../../state/modules/service';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import ValueCard from '../../components/connectedForm/ValueCard';
import { IDropdownProps } from '../../@types/dropdown';
import { IEditableDataGridProps } from '../../components/EditableDataGrid/EditableDataGrid';
import { IConnectedFormProps } from '../../@types/connectedFormTypes';
import hasValidationErrors from '../../components/connectedForm/utils/hasValidationErrors';
import { IFormatterProps } from '../../@types/workspaceTypes';
import NumericFormatter from '../../components/grid-addons/formatters/NumericFormatter';
import { cloneDeep } from '@apollo/client/utilities';
import { useFullUserDetails } from '../../utils/hooks';
import debounce from 'es6-promise-debounce';

const {
  default: defaultQuery,
  temporary,
  formTitle,
  instance,
  workspaceUrl,
  update,
  temporarySave,
  fragment,
  type,
} = StockAdjustment;

const stockLotMap = new Map<number, IStockLot>();

const fluentSaveIcon = { iconName: 'save' };

const useProductsStockAdjustmentAttributes = ({
  change,
  product,
  setColumns,
  setEditableColumns,
  values,
  rowIdx,
  readOnly,
}: {
  change: ConnectedFormContextType['change'];
  product: Maybe<IProduct>;
  setColumns: React.Dispatch<React.SetStateAction<IEditableDataGridColumnDef[]>>;
  setEditableColumns: React.Dispatch<React.SetStateAction<IEditableDataGridColumnDef[]>>;
  values: IExtendedStockAdjustment;
  rowIdx?: number;
  readOnly?: boolean;
}) => {
  const [getStockLotsByBin] = useLazyQuery<IStockLotsByBinQueryResult, IStockLotsByBinQueryVariables>(StockLotsByBin);
  const [getGoodsReceiptAttributes] = useLazyQuery<
    IGoodsReceiptAttributesQueryResult,
    IGoodsReceiptAttributesQueryVariables
  >(GoodsReceiptAttributesQuery);

  /**
   * Function to fetch/reset attribute columns for a particular row.
   * The new productId/binLocation are provided as these may have changed since the last time this function was updated with 'values.adjustmentLines'
   * The 'forceLineChange' property is to cover an edge case where the timing of the call to this function prevents the product/binLocation check from working correctly.
   */
  const getAttributeColumns = useCallback(
    (productId: number, binLocationId: number, newRowIdx: number, issue: boolean) => {
      // fetch available lots
      getStockLotsByBin({ variables: { productId, binLocationId }, fetchPolicy: 'network-only' }).then((result) => {
        const stockLots: Array<IStockLot> = [];
        if (result?.data?.list) {
          result.data.list
            .filter((stockLot) => {
              // filter out lots with no stock
              return !issue || stockLot?.freeStock > 0.0;
            })
            .forEach((stockLot) => {
              // add to global for look up later
              stockLotMap.set(stockLot!.id!, stockLot as IStockLot);
              // add to local array for iteration now
              stockLots.push(stockLot as IStockLot);
            });
        }

        const lotAttributeConfigs = new Map<unknown, ILotAttributeConfig>();
        const newRows: Array<{ [key: string]: any }> = [];

        if (stockLots.length > 0) {
          for (const stockLot of stockLots) {
            // add the stock lot id for reference on submit
            const attributeLine: { [key: string]: any } = {
              stockLotId: stockLot.id,
              freeStock: stockLot.freeStock,
            };

            // if this lot has lot attribute values, register the config and value
            if (stockLot.lotAttributeValues) {
              stockLot.lotAttributeValues.forEach((lotAttributeValue) => {
                const attributeConfig = lotAttributeValue!.attributeConfig!;
                lotAttributeConfigs.set(attributeConfig!.id, attributeConfig);

                attributeLine[getKey(attributeConfig)] = lotAttributeValue!.value;
              });
            }

            newRows.push(attributeLine);
          }
        } else {
          // add at least one row for when there is no stock lots (i.e. it has never been held in stock)
          newRows.push({});
        }

        const newColumns = getLotAttributeColumns(
          lotAttributeConfigs.values(),
          `attributeLines-${newRowIdx}`,
          newRowIdx
        ).columns;

        // add editable quantity box to end
        newColumns.push(
          {
            name: 'Free Stock',
            key: 'freeStock',
            formatter: Formatters.NumericFormatter,
            editable: false,
            resizable: true,
            cellClass: 'rightAlign',
            options: {
              format: { decimals: product?.precision || 0 },
            },
          },
          {
            name: 'Quantity',
            key: 'quantity',
            formatter: Formatters.NumericFormatter,
            editable: true,
            resizable: true,
            cellClass: 'rightAlign',
            options: {
              format: { decimals: product?.precision || 0 },
            },
          }
        );

        // make all columns except quantity not editable
        newColumns
          .filter((column) => {
            return column.key !== 'quantity';
          })
          .forEach((column) => {
            column.editable = false;
          });

        setColumns(newColumns);
        change(`attributeLines-${newRowIdx}`, newRows);
      });

      getGoodsReceiptAttributes({ variables: { id: productId }, fetchPolicy: 'network-only' }).then((result) => {
        const data = result?.data?.data;
        if (data) {
          const attributeColumns = getLotAttributeColumns(
            data?.lotAttributes as unknown as IterableIterator<ILotAttributeConfig>,
            `attributeLinesNew-${newRowIdx}`,
            newRowIdx
          );
          const lotAttributeColumns: Array<IEditableDataGridColumnDef> = attributeColumns.columns;

          lotAttributeColumns.push({
            name: 'Quantity',
            key: 'quantity',
            formatter: Formatters.NumericFormatter,
            editable: true,
            resizable: true,
            cellClass: 'rightAlign',
            options: {
              format: { decimals: product?.precision || 0 },
            },
          });

          setEditableColumns(lotAttributeColumns);
        }
      });
    },
    [change, getGoodsReceiptAttributes, getStockLotsByBin, setColumns, setEditableColumns, product?.precision]
  );

  const adjustmentLines = values.adjustmentLines;

  //Fetch id's outside the effect - If the effect uses the higher level 'adjustmentLines' as its' dependency, it will trigger more often than we required.
  const productId = adjustmentLines?.[rowIdx!]?.product?.id;
  const binId = adjustmentLines?.[rowIdx!]?.bin?.id;
  const issueReceipt = adjustmentLines?.[rowIdx!]?.issueReceipt;

  //Track the last product/bin location/issue state used to fetch attribute columns for each line, so we can initiate a fetch 'only' when we need to.
  const lineAttributeColumnsMap = useRef<{
    [rowIndex: `${number}`]: { productId: number; binId: number; issue: boolean };
  }>({});

  useEffect(() => {
    if (rowIdx === undefined) {
      return;
    }
    const previous = lineAttributeColumnsMap.current[`${rowIdx}`];
    const issue = issueReceipt === IssueReceipt.Issue;

    const hasChanged = productId !== previous?.productId || binId !== previous?.binId || issue !== previous?.issue;

    if (productId && binId && !readOnly && hasChanged) {
      lineAttributeColumnsMap.current[`${rowIdx}`] = { productId, binId, issue };
      getAttributeColumns(productId, binId, rowIdx, issue);
    }
  }, [binId, productId, rowIdx, readOnly, issueReceipt, getAttributeColumns]);
};

//Additional fields get temporarily added to the lines, which are then normalized out by 'submitValues'.
type IExtendedStockAdjustmentLine = IStockAdjustmentLine & { stockLotId: number };

/**
 * Typically, quantity should be a number, however text entry can result in it being a string, so that must be handled.
 */
type IAttributeLine = { quantity: number | string; stockLotId: number };

type IExtendedStockAdjustment = IStockAdjustment & {
  [key: `attributeLines-${number}`]: Array<IAttributeLine> | undefined;
  [key: `attributeLinesNew-${number}`]: Array<IAttributeLine> | undefined;
};

/**
 * Until the adjustment is persisted, the quantity for each line is calculated from the attribute lines - Moved that here rather than
 * attaching it to the back of various 'withRowUpdated' functions for simplicity.
 * (Note - On save, the lines are split up by attribute anyway, with each having a quantity set based on the attribute line, i.e. the quantity of the adjustment line itself is irrelevant until then).
 */
const AdjustmentQuantityFormatter = (props: IFormatterProps<IStockAdjustmentLine>) => {
  const connectedForm = useContext(ConnectedFormContext);
  const values = connectedForm.values as IExtendedStockAdjustment;
  const attributeLines = values[`attributeLines-${props.rowIdx}`];

  if (!attributeLines) {
    //When viewing an existing line, if the line has not yet been touched (selected) then attributeLines will not yet be initialized.
    return <NumericFormatter {...props} />;
  }

  const attributeTotal = attributeLines
    .map((attributeLine) => {
      if (typeof attributeLine.quantity === 'number') {
        return attributeLine.quantity;
      }
      return parseFloat(attributeLine.quantity ?? '0');
    })
    .reduce((acc, val) => {
      return acc + val;
    }, 0);

  const newAttributeLines = values[`attributeLinesNew-${props.rowIdx}`] ?? [];

  const newAttributeTotal = newAttributeLines
    .map((attributeLine) => {
      if (typeof attributeLine.quantity === 'number') {
        return attributeLine.quantity;
      }
      return parseFloat(attributeLine.quantity ?? '0');
    })
    .reduce((acc, val) => {
      return acc + val;
    }, 0);

  return <NumericFormatter {...props} value={attributeTotal + newAttributeTotal} />;
};

type IProductsStockAdjustmentFormProps = {
  values: IExtendedStockAdjustment;
  match: any;
  restricted?: boolean;
  salesCreditAdjustment?: boolean;
  customSubmit?: (values: IStockAdjustment) => void;
  change: ConnectedFormContextType['change'];
  readOnly?: boolean;
  addModalActions?: (actions: Array<IExtendedMetaAction>) => void;
  disabled?: boolean;
  modalClose?: boolean;
  onClose?: () => void;
  addCapabilityOnly?: boolean; //Used by 'checkOperationEdit'

  /**
   * @deprecated - Should be removed - Care required to avoid side effects, benefit of the flag may be obscure.
   */
  forceUpdate?: boolean;
};

const ProductsStockAdjustmentForm = (props: IProductsStockAdjustmentFormProps) => {
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();
  const [columns, setColumns] = useState<Array<IEditableDataGridColumnDef>>([]);
  const [editableColumns, setEditableColumns] = useState<Array<IEditableDataGridColumnDef>>([]);
  const [rowIdx, setRowIdx] = useState<number | undefined>();
  const [kitProduct, setKitProduct] = useState<Maybe<IProduct>>(null);
  const [kitQuantity, setKitQuantity] = useState<Maybe<number>>(null);
  const warehouseId = useRef();
  const company = useCompany();
  const fullUserDetails = useFullUserDetails();

  const { match, restricted, salesCreditAdjustment, customSubmit, values, change, readOnly, addModalActions } = props;

  const restrictedSalesCreditAdjustment: boolean =
    !salesCreditAdjustment && !fullUserDetails?.companyUser?.canAmendStockAdjustmentForReturn;

  useEffect(() => {
    warehouseId.current = values.warehouse?.id;
  }, [values.warehouse?.id]);

  const costPricePrecision = company?.costPricePrecision ?? 0;

  const productFilter = () => {
    return warehouseId.current !== undefined && warehouseId.current >= 0
      ? [
          {
            filterOperation: 'EQUALS',
            filterParameters: {
              field: 'productWarehouseDetails.warehouse.id',
              value: warehouseId.current,
            },
          },
        ]
      : [];
  };

  // helpers
  const adjustmentLines = values.adjustmentLines;
  const product = getIn(adjustmentLines, [rowIdx, 'product']) as Maybe<IProduct>;
  const type = getIn(adjustmentLines, [rowIdx, 'issueReceipt']) as Maybe<IssueReceipt>;

  const reference = product ? product.reference : '';
  const description = product ? product.description : '';

  const [performSave, confirmationPrompts] = usePerformSave();
  const performTemporarySave = useDebouncedTemporarySave(false, 500);

  const [productWarehouseDetailsList] = useLazyQuery<
    IProductWarehouseDetailsWorkspaceQueryResult,
    IProductWarehouseDetailsWorkspaceQueryVariables
  >(ProductWarehouseDetailsQuery, { fetchPolicy: 'network-only' });

  useProductsStockAdjustmentAttributes({ change, product, setColumns, setEditableColumns, values, rowIdx, readOnly });

  /**
   * When Warehouse changes, query to get the default bin for each adjustment line and automatically select that bin.
   */
  const resetDefaultBinAndAttributes = useCallback(
    (newWarehouse: IWarehouse) => {
      if (values.adjustmentLines && values.adjustmentLines?.length > 0) {
        const pairedFilters = values.adjustmentLines
          .map<number>((adjustmentLine) => adjustmentLine!.product!.id!)
          .map<IFilterInput>((productId) => {
            return {
              filterParameters: {
                booleanOperator: BooleanOperator.And,
                filters: [
                  {
                    filterOperation: FilterOperation.Equals,
                    filterParameters: {
                      field: 'product.id',
                      value: productId,
                    },
                  },
                  {
                    filterOperation: FilterOperation.Equals,
                    filterParameters: {
                      field: 'warehouse.id',
                      value: newWarehouse.id,
                    },
                  },
                ],
              },
            };
          });
        productWarehouseDetailsList({
          variables: {
            filters: [
              {
                filterParameters: { booleanOperator: BooleanOperator.Or, filters: pairedFilters },
              },
            ],
          },
        }).then((result) => {
          const productWarehouseDetails: Array<IProductWarehouseDetail> =
            result?.data?.listData?.edges?.map((edge) => edge?.node as IProductWarehouseDetail) ?? [];
          let count = 0;
          values.adjustmentLines?.forEach((adjustmentLine, index) => {
            const productId = adjustmentLine?.product?.id;
            const binLocationId = adjustmentLine?.bin?.id;

            if (productId && binLocationId) {
              const newBin = productWarehouseDetails.filter(
                (pwd) => pwd.product?.id === adjustmentLine?.product?.id && pwd.warehouse?.id === newWarehouse?.id
              )[0]?.defaultBinLocation;

              if (newBin) {
                dispatch(change(`adjustmentLines.${index}.bin`, newBin));
                count++;
              }
            }
          });
          if (count > 0) {
            dispatch(
              serviceActions.genericAlert(
                'Warehouse changed',
                `Selected lots have been reset for ${count} line(s) due to a change to the selected Warehouse, and quantities may need to be re-entered.`,
                AlertType.Warning
              )
            );
          }
        });
      }
      //Exhaustive-deps - Do not call when attribute lines length changes (Would needlessly refetch).
      //Note - Could also trigger this directly off the change method of Warehouse.
    },
    [change, dispatch, productWarehouseDetailsList, values.adjustmentLines]
  );

  const kitOptParam = match?.params?.kitOpt;

  const kitOpt = useMemo<KitOpt | undefined>(() => {
    if (kitOptParam) {
      return kitOptParam === 'Kit' ? KitOpt.Kit : KitOpt.Dekit;
    }
    return undefined;
  }, [kitOptParam]);

  const kitFilters = [
    {
      filterParameters: {
        field: 'productClass',
        value: ProductClass.Kit,
      },
      filterOperation: FilterOperation.Equals,
    },
    {
      filterParameters: {
        field: 'productClass',
        value: ProductClass.Assembly,
      },
      filterOperation: FilterOperation.Equals,
    },
  ];

  const columnMeta = useMemo<Array<IEditableDataGridColumnDef>>(() => {
    const binLocationVars = ({ rowData }: { rowData: IStockAdjustmentLine }) => {
      let productId: Maybe<number> = null;
      let hasFreeStock: Maybe<boolean> = null;

      if (rowData?.issueReceipt === 'ISSUE') {
        productId = rowData?.product?.id;
        hasFreeStock = true;
      }

      return {
        warehouseId: values.warehouse?.id,
        productId,
        binLocationCriteria: {
          hasFreeStock,
        },
      };
    };

    const binLocationFilters = () => {
      return [
        {
          filterOperation: 'EQUALS',
          filterParameters: {
            field: 'warehouse.id',
            value: values.warehouse?.id,
          },
        },
      ];
    };

    const issue = getIn(values, ['adjustmentLines', rowIdx, 'issueReceipt']) === 'ISSUE';

    const isProductEditable = !restricted && (salesCreditAdjustment || !restrictedSalesCreditAdjustment);

    // Update column Meta any time the warehouse changes, selected row or lines change
    return [
      {
        name: 'Product',
        key: 'product',
        editable: isProductEditable,
        editor: <DropdownEditor customFilter={productFilter} />,
        options: Product,
        formatter: 'dropdown',
        width: 200,
        resizable: false,
      },
      {
        name: 'Description',
        key: 'product.description',
        editable: false,
        resizable: true,
        width: 350,
      },
      {
        name: 'Type',
        key: 'issueReceipt',
        editable: salesCreditAdjustment && !restricted,
        resizable: true,
        formatter: 'dropdown',
        editor: <DropdownEditor />,
        options: IssueReceiptEntity,
      },
      {
        name: 'Bin',
        key: 'bin',
        editable: true,
        resizable: true,
        formatter: 'dropdown',
        editor: <DropdownEditor customFilter={!issue ? binLocationFilters : null} />,
        options: {
          dropdown: issue
            ? {
                query: BinLocationForProductAndWarehouse,
                customVariables: binLocationVars,
                rawLabel: true,
                valueKey: 'id',
                labelKey: 'location',
                normalizeData: (data) => {
                  return data.map((row) => {
                    return row.binLocation;
                  });
                },
              }
            : {
                ...BinLocation.dropdown,
              },
          searchTabOffset: { xOffset: 1, yOffset: 0 },
          searchEnterOffset: { xOffset: 0, yOffset: 0 },
        },
      },
      {
        name: 'Valuation Cost',
        key: 'product.valuationCostPrice',
        formatter: Formatters.NumericFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      {
        name: 'Latest Cost',
        key: 'product.latestCostPrice',
        formatter: Formatters.NumericFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      {
        name: 'Cost Unit',
        key: 'product.costUnitQuantity',
        formatter: Formatters.NumericFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      {
        name: 'Movement Cost',
        key: 'movementCostPrice',
        formatter: Formatters.NumericFormatter,

        editable: (rowData) => {
          if (rowData.product && kitOpt === KitOpt.Dekit) {
            return true;
          }

          return !restrictedSalesCreditAdjustment && rowData.issueReceipt === 'RECEIPT';
        },
        resizable: true,
        cellClass: 'rightAlign',
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      {
        name: 'Quantity',
        key: 'quantity',
        formatter: AdjustmentQuantityFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        options: {
          format: { decimals: getProductDP },
        },
      },
      {
        name: 'Required',
        key: 'required',
        formatter: Formatters.NumericFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        options: {
          format: { decimals: getProductDP },
        },
        width: 100,
        visible: kitOpt || !salesCreditAdjustment,
      },
    ];
  }, [rowIdx, values, salesCreditAdjustment, kitOpt, restricted, costPricePrecision]);

  const handleOnSubmit = useCallback(() => {
    if (customSubmit) {
      if (!hasValidationErrors(connectedForm)) {
        //Custom submit function provided. Used by Sales Credits to submit both the adjustment and credit in a single service call.
        customSubmit(submitValues(props, false));
      }
    } else {
      connectedForm.submit?.();
    }
  }, [connectedForm, customSubmit, props]);

  const [productComponentAllComponents] = useLazyQuery<
    IProductComponentAllComponentsQueryResult,
    IProductComponentAllComponentsQueryVariables
  >(ProductComponentAllComponents);

  function fetchComponents() {
    if (!kitQuantity || kitQuantity < 0.0) {
      return;
    }

    productComponentAllComponents({ variables: { productId: kitProduct?.id } }).then((response) => {
      if (response?.data) {
        const kit = kitOpt === KitOpt.Kit;

        const adjustmentLines: Array<IStockAdjustmentLineInput> = [];

        // the kit
        adjustmentLines.push({
          product: kitProduct as InputMaybe<IProductInput>,
          quantity: kitQuantity,
          required: kitQuantity,
          issueReceipt: kit ? IssueReceipt.Receipt : IssueReceipt.Issue,
          kitOpt: kitOpt,
          uuid: uuIdGenerator(),
        });

        response.data.data
          ?.filter((component) => component?.product?.leveled)
          .forEach((component) => {
            adjustmentLines.push({
              product: component?.product,
              quantity: (component?.quantity ?? 0) * kitQuantity,
              required: (component?.quantity ?? 0) * kitQuantity,
              issueReceipt: kit ? IssueReceipt.Issue : IssueReceipt.Receipt,
              kitOpt: kitOpt,
              parent: kitProduct as IProductInput,
              uuid: uuIdGenerator(),
            });
          });

        Promise.all([dispatch(change('adjustmentLines', adjustmentLines))]).then(performTemporarySave);

        setKitProduct(null);
        setKitQuantity(null);
      }
    });
  }

  function getAttributeColumnsReadOnly(rowData: IStockAdjustmentLine, newRowIdx: number) {
    const lotAttributeConfigs: Array<ILotAttributeConfig> = [];

    // reads available configs from data rather than API call
    const attributeLine: { [key: string]: Maybe<string> } = {};
    rowData?.lotAttributeValues?.forEach((lotAttributeValue) => {
      const attributeConfig = lotAttributeValue?.attributeConfig;
      if (attributeConfig) {
        lotAttributeConfigs.push(attributeConfig);

        attributeLine[getKey(attributeConfig)] = lotAttributeValue?.value;
      }
    });

    attributeLine.quantity = rowData.quantity;

    const newColumns = getLotAttributeColumns(
      lotAttributeConfigs.values(),
      `attributeLines-${newRowIdx}`,
      newRowIdx
    ).columns;

    newColumns.push({
      name: 'Quantity',
      key: 'quantity',
      formatter: Formatters.NumericFormatter,
      editable: false,
      resizable: true,
      cellClass: 'rightAlign',
      options: {
        format: { decimals: 2 },
      },
    });

    setColumns(newColumns);

    dispatch(change(`attributeLines-${newRowIdx}`, [attributeLine]));
  }

  const debounceOnCellSelected = useCallback(
    debounce((dataid: string, selected: any, rowData: IStockAdjustmentLine, setRowIdx: Function, props: any) => {
      setRowIdx((oldRowIdx: number) => {
        if (oldRowIdx !== selected.rowIdx && rowData.product && rowData.bin && props.readOnly) {
          getAttributeColumnsReadOnly(rowData, selected.rowIdx);
        }

        return selected.rowIdx;
      });
    }, 1000),
    []
  );

  function onCellSelected(dataid: string, selected: any, rowData: IStockAdjustmentLine) {
    debounceOnCellSelected(dataid, selected, rowData, setRowIdx, props);
  }

  const onDeleteClick: ComponentProps<typeof ContextMenuItem>['onClick'] = (value, close, rowIndex) => {
    if (close) close();

    dispatch(change(`adjustmentLines.${rowIndex}.deleted`, true));
  };

  const getLineActionButtons = ({ rowData, rowIdx }: { rowData: IStockAdjustmentLine; rowIdx: number }) => {
    if (rowData?.id) return []; //If the line has an id, it has been saved - Cannot delete a saved stock adjustment line.

    return [<ContextMenuItem key={'actionButtonDelete'} onClick={onDeleteClick} label={'Delete'} rowIdx={rowIdx} />];
  };

  const stockAdjustmentActions = [
    {
      key: 'printStockAdjustmentLabel',
      text: 'Print Label',
      actionReference: StockAdjustmentLabel.screenReference,
      condition: () => values?.id > 0,
      buttonProps: {
        iconProps: !connectedForm.readOnly && connectedForm.dirty ? fluentSaveIcon : undefined,
        theme: InfoTheme,
      },
      onClick: () => {
        const addPrintAction = () =>
          addModalActions?.([
            {
              actionType: ActionType.PrintStockAdjustmentLabels,
              options: {
                rowIdx: rowIdx,
              },
            },
          ]);
        //Perform save if necessary (New Adjustment).
        if (connectedForm.dirty) {
          performSave({
            onSaveComplete: ({ result }) => {
              dispatch(
                serviceActions.genericAlert(
                  `Stock Adjustment saved`,
                  `Stock Adjustment ${result?.documentNumber} was saved successfully`,
                  AlertType.Success,
                  {
                    alertForm: 'StockAdjustment',
                  }
                )
              );
            },
            submitMeta: { suppressAfterSubmit: true },
          });
        } else {
          addPrintAction();
        }
      },
    },
  ];

  return (
    <Form.FormContainer>
      {confirmationPrompts}
      <Form.Header
        entityHdr="documentNumber"
        component={Form.HeaderBlock}
        onSubmit={handleOnSubmit}
        actionButtons={stockAdjustmentActions}
      />
      <Form.Form>
        <Form.Row>
          <Form.Fieldset cols={1 / 3}>
            <Form.Field<IDropdownProps<IWarehouse>, IWarehouse>
              dataid="warehouse"
              component={input.Dropdown}
              label="Warehouse"
              autoFocus={true}
              onChange={(event, newValue) => {
                resetDefaultBinAndAttributes(newValue);
              }}
            />
            <Form.Field<IDropdownProps>
              dataid="adjustmentReason"
              initialized={true}
              component={input.Dropdown}
              label="Adjustment Reason"
            />
            <Form.Field dataid="reference" component={input.BlurTextField} label="Reference" />
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 3}>
            <Form.Field dataid="movementDate" component={input.DateField} label="Movement Date" />
            <Form.Field dataid="labelsRequired" component={CheckboxInput} label="Print During Receipt" />
            {kitOpt ? (
              <>
                <QueryDropdown
                  label="Kit"
                  {...entities.Product.dropdown}
                  predefinedFilters={kitFilters}
                  onChange={setKitProduct}
                  value={kitProduct}
                  data-testid={'kit-product'}
                />
                <BlurTextField
                  label="Quantity"
                  value={kitQuantity}
                  onChange={(value: string) => {
                    if (value.match(/^\d*\.?\d*$/)) {
                      setKitQuantity(parseFloat(value));
                    }
                  }}
                  enableOnChange
                  data-testid={'kit-quantity'}
                />
                <PrimaryButton data-testid={`kit-opt-btn`} onClick={fetchComponents}>
                  {kitOpt}
                </PrimaryButton>
              </>
            ) : null}
            {values.salesCredit?.id ? (
              <>
                <Form.Legend label={'Origin'} />
                <ValueCard
                  readOnly={true}
                  value={values.salesCredit?.documentNumber}
                  label="Sales Credit"
                  onClick={() => {
                    const path = `${SalesCredit.formUrl}${values.salesCredit?.id}`;
                    window.open(window.location.origin + path, '_blank');
                  }}
                  tooltip={'View Sales Credit'}
                  type={'text'}
                />
              </>
            ) : null}
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 3}>
            <Form.Legend label="Total Movement Costs" />
            <Form.Field<IBlurTextFieldProps>
              dataid="totalMovementCostReceipts"
              component={input.BlurTextField}
              decimals={costPricePrecision}
              label="Receipts"
              type={BlurTextFieldType.BaseCurrency}
              readOnly={true}
            />
            <Form.Field<IBlurTextFieldProps>
              dataid="totalMovementCostIssues"
              component={input.BlurTextField}
              decimals={costPricePrecision}
              label="Issues"
              type={BlurTextFieldType.BaseCurrency}
              readOnly={true}
            />
            <Form.Field<IBlurTextFieldProps>
              dataid="totalMovementCostNett"
              component={input.BlurTextField}
              decimals={costPricePrecision}
              label="Nett"
              type={BlurTextFieldType.BaseCurrency}
              readOnly={true}
            />
          </Form.Fieldset>
        </Form.Row>
        <Form.TabPanel>
          <Form.TabContent id="items" label="Adjustments">
            <GridContainer>
              <Form.Field<IEditableDataGridProps>
                dataid="adjustmentLines"
                component={input.DataGrid}
                columns={columnMeta}
                enableCellSelect={true}
                enableCellAutoFocus={false}
                addBlankRow={!restrictedSalesCreditAdjustment}
                onCellSelected={onCellSelected}
                renderActionButtons={getLineActionButtons}
                enableContextMenu={true}
                contextKey={'adjustmentLines'}
                temporaryOnChange={true}
                autoContentHeight={true}
                maxHeight={500}
              />
            </GridContainer>
            {columns && columns.length > 0 ? (
              <>
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Legend label={`Bin Location/Attribute Details For ${reference} ${description}`} />
                  </Form.Fieldset>
                </Form.Row>
                <GridContainer>
                  <Form.Field<IEditableDataGridProps>
                    dataid={`attributeLines-${rowIdx}`}
                    component={input.DataGrid}
                    columns={columns}
                    enableCellSelect={true}
                    autoContentHeight={true}
                    temporaryOnChange={true}
                    initialized={true}
                  />
                </GridContainer>
                {!readOnly && columns.length > 1 && type === 'RECEIPT' ? (
                  <>
                    <Form.Row>
                      <Form.Fieldset cols={1}>
                        <Form.Legend label="New Lot Attributes" />
                      </Form.Fieldset>
                    </Form.Row>
                    <GridContainer>
                      <Form.Field<IEditableDataGridProps>
                        dataid={`attributeLinesNew-${rowIdx}`}
                        component={input.DataGrid}
                        columns={editableColumns.filter((columnDefinition) => !!columnDefinition.editable)}
                        enableCellSelect={true}
                        addBlankRow={true}
                        autoContentHeight={true}
                      />
                    </GridContainer>
                  </>
                ) : null}
              </>
            ) : null}
          </Form.TabContent>
        </Form.TabPanel>
      </Form.Form>
    </Form.FormContainer>
  );
};

ProductsStockAdjustmentForm.defaultProps = {
  salesCreditAdjustment: true,
  restricted: false,
};

function submitValues({ values }: { values: IExtendedStockAdjustment }, isTemporary: boolean): IStockAdjustment {
  const stockAdjustment = cloneDeep(values);
  const adjustmentLines = stockAdjustment.adjustmentLines as Array<IExtendedStockAdjustmentLine>;
  const items: Array<Maybe<IStockAdjustmentLine>> = [];

  if (adjustmentLines && adjustmentLines.length > 0) {
    adjustmentLines.forEach((adjustmentLine, idx) => {
      const attributeLineKey: `attributeLines-${number}` = `attributeLines-${idx}`;
      const attributeLinesNewKey: `attributeLinesNew-${number}` = `attributeLinesNew-${idx}`;

      if (isTemporary) {
        //We need to push a single mock line in for temporary save.
        //We do *not* want to push one line-per-stock-lot as the other processing will do, because it will result in line duplication.

        const stockAdjustmentsForLine = stockAdjustment[attributeLineKey];
        items.push({
          ...adjustmentLine,
          quantity: stockAdjustmentsForLine
            ? stockAdjustmentsForLine?.reduce((total, adjustment) => {
                return total + (Number(adjustment.quantity) || 0);
              }, 0)
            : adjustmentLine.quantity,
        } as IStockAdjustmentLine);
      } else if (!!adjustmentLine.product && (adjustmentLine.id || !adjustmentLine.deleted)) {
        const attributeLines = stockAdjustment[attributeLineKey]
          ? stockAdjustment[attributeLineKey]?.filter((attributeLine) => {
              return (attributeLine?.quantity as number) > 0.0;
            })
          : [];

        const attributeLinesNew = stockAdjustment[attributeLinesNewKey]
          ? stockAdjustment[attributeLinesNewKey]?.filter((attributeLine) => {
              return (attributeLine?.quantity as number) > 0.0;
            })
          : [];

        if (attributeLines?.length > 0 || attributeLinesNew?.length > 0) {
          // for every lot attribute with a quantity > 0 create a new adjustment line with lot attributes for the stock lot
          attributeLines?.forEach((attributeLine) => {
            // lookup the stock lot from global map with the ID recorded against the attribute line
            const stockLot = stockLotMap.get(attributeLine.stockLotId);

            // record the new line with lot attributes from stock lot with desired quantity
            items.push({
              ...adjustmentLine,
              id: undefined,
              uuid: uuIdGenerator(),
              concurrencyVersion: 0,
              quantity: attributeLine.quantity,
              originalUuid: adjustmentLine.uuid,
              lotAttributeValues: stockLot ? stockLot.lotAttributeValues : [],
            });
          });

          // only add new lots if it's a receipt
          if (adjustmentLine?.issueReceipt === 'RECEIPT') {
            attributeLinesNew?.forEach((attributeLine) => {
              const lotAttributeValues: Array<ILotAttributeValue> = [];

              // loop through all the lot attribute properties
              Object.entries(attributeLine)
                .filter(([key]) => {
                  return key.startsWith('lotAttributes');
                })
                .forEach(([key, value]) => {
                  // build up new lot attribute values from the key
                  const attrId = getAttrIdArray(key) as Array<any>;
                  const id = attrId[1];
                  const uuid = attrId[2];
                  const concurrencyVersion = attrId[3];
                  const valueFieldName = attrId[4];

                  lotAttributeValues.push({
                    attributeConfig: {
                      id,
                      uuid,
                      concurrencyVersion,
                    } as ILotAttributeConfig,
                    [valueFieldName]: value,
                  } as unknown as ILotAttributeValue);
                });

              const newItem = {
                ...adjustmentLine,
                id: undefined,
                uuid: uuIdGenerator(),
                concurrencyVersion: 0,
                quantity: attributeLine.quantity,
                originalUuid: adjustmentLine.uuid,
                lotAttributeValues,
              };

              if (attributeLine?.binLocation) {
                newItem.bin = attributeLine.binLocation;
              }

              items.push(newItem);
            });
          }
        } else {
          // if we've got here then we have generated some attribute lines but all are set to zero, the stock adjustment
          // should reflect that and match the AdjustmentQuantityFormatter value
          if (stockAdjustment[attributeLineKey] || stockAdjustment[attributeLinesNewKey]) {
            adjustmentLine.quantity = 0;
          }
          items.push({ ...adjustmentLine, originalUuid: adjustmentLine.uuid });
        }
      }
    });
  }
  // delete the attribute lines as they are not part of the payload
  // moved code outside the above loops - Was being run before attribute li
  Object.keys(stockAdjustment)
    .filter((key) => key.startsWith('attributeLines'))
    .forEach((key) => {
      delete stockAdjustment[key as keyof typeof stockAdjustment];
    });
  stockAdjustment.adjustmentLines = items;
  return { ...stockAdjustment };
}

const ConnectedAdjustmentForm = createForm({
  form: type,
  name: formTitle,
  entity: StockAdjustment,
  queryFragment: fragment,
  defaultReturnUrl: workspaceUrl,
  getSubmitValues: submitValues,
})(ProductsStockAdjustmentForm) as FunctionComponent<IConnectedFormProps & Partial<IProductsStockAdjustmentFormProps>>;

/**
 * Stock Adjustment Form.
 * @param temporaryDefault  - If true, expects to use a temporary saved record as the default data for this form.
 * @param overrideParams    - Override parameters - For use by modal wrappers as otherwise the current locations params are used by default.
 * @param props             - Input props.
 */
export default memo(
  ({
    temporaryDefault,
    ...props
  }: { temporaryDefault: boolean } & Omit<
    IProductsStockAdjustmentFormProps,
    'values' | 'onSubmit' | 'readOnly' | 'change'
  >) => {
    const overrideParams = props.match?.params;
    const formDefault = useFormQuery(
      temporaryDefault ? { default: temporary, instance: instance } : { default: defaultQuery, instance: instance },
      temporaryDefault ? { forceDefault: true, idParam: 'uuid', overrideParams } : { overrideParams }
    );
    const [formUpdate] = useMutation(update!);
    const [formTemporaryUpdate] = useMutation(temporarySave!);

    const stockAdjustmentPostSave = useProductsStockAdjustmentPostSave();
    return (
      <ConnectedAdjustmentForm
        {...props}
        formUpdate={formUpdate}
        formTemporaryUpdate={formTemporaryUpdate}
        formData={formDefault}
        forceUpdate={temporaryDefault}
        postSaveFunction={stockAdjustmentPostSave.postSaveFunction}
        addModalActions={stockAdjustmentPostSave.addModalActions}
      >
        {stockAdjustmentPostSave.modals}
      </ConnectedAdjustmentForm>
    );
  }
);
